import { useState, useEffect } from "react";
import React from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { EyeIcon, EyeOffIcon, Key } from "lucide-react";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import PageMeta from "../../components/common/PageMeta";
import {
  resetPasswordContainer,
  formContainer,
  iconContainer,
  keyIcon,
  titleContainer,
  heading,
  formGroup,
  inputContainer,
  inputWrapper,
  inputField,
  inputFieldError,
  successMessage,
  buttonContainer,
  loginButton,
  eyeIconBtn,
  requiredStar,
  errorText,
  backButton
} from "../../styles/pages/ResetPassword.Styles";

const ResetPassword = () => {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const tokenFromUrl = searchParams.get("token");
    if (tokenFromUrl) {
      setToken(tokenFromUrl);
    }
  }, [searchParams]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
  
    // Reset previous errors
    setPasswordError("");
    setConfirmPasswordError("");
    setError("");
  
    // Field validation
    if (!token) {
      setError("The token field is required!");
      return;
    }
    if (!password) {
      setPasswordError("The password field is required!");
    }
    if (!confirmPassword) {
      setConfirmPasswordError("The confirm password field is required!");
    }
    if (!password || !confirmPassword) return;
  
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/reset/password`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            password,
            password_confirmation: confirmPassword,
          }),
        }
      );
  
      const data = await response.json();
  
      if (data.status === 200) {
        setMessage("Password changed successfully!");
        setError("");
        setTimeout(() => navigate("/reset-success"), 1000);
      } else {
        if (data.message?.token) {
          setError(data.message.token[0]); // Display API token error
        } else if (typeof data.message === "string") {
          setError(data.message);
        } else if (typeof data.message === "object") {
          setPasswordError(data.message.password?.[0] || "");
          setConfirmPasswordError(data.message.password_confirmation?.[0] || "");
        } else {
          setError("An unexpected error occurred.");
        }
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
  };
  

  return (
    <>
      <PageMeta
        title="Reset Password"
        description="This is Reset Password page"
      />
      <div className={resetPasswordContainer}>
        <div className={formContainer}>
          <div className={iconContainer}>
            <Key className={keyIcon} />
          </div>

          <div className={titleContainer}>
            <h1 className={heading}>Reset Password</h1>
          </div>

          <form onSubmit={handleSubmit}>
            <div className={formGroup}>
              <div className={inputContainer}>
                <Label>
                  New Password <span className={requiredStar}>*</span>
                </Label>
                <div className={inputWrapper}>
                  <Input
                    type={showPassword ? "text" : "password"}
                    className={passwordError ? inputFieldError : inputField}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                  />
                  <button
                    type="button"
                    className={eyeIconBtn}
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeIcon size={20} /> : <EyeOffIcon size={20} />}
                  </button>
                </div>
                {passwordError && <p className={errorText}>{passwordError}</p>}
              </div>

              <div className={inputContainer}>
                <Label>
                  Confirm Password <span className={requiredStar}>*</span>
                </Label>
                <div className={inputWrapper}>
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    className={confirmPasswordError ? inputFieldError : inputField}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setConfirmPasswordError("");
                    }}
                  />
                  <button
                    type="button"
                    className={eyeIconBtn}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeIcon size={20} />
                    ) : (
                      <EyeOffIcon size={20} />
                    )}
                  </button>
                </div>
                {confirmPasswordError && (
                  <p className={errorText}>{confirmPasswordError}</p>
                )}
              </div>

              {error && <p className={errorText}>{error}</p>}
              {message && !error && <p className={successMessage}>{message}</p>}

              <div className={buttonContainer}>
                <button type="submit" className={loginButton}>
                  Reset Password
                </button>
                 <button
                          className={backButton}
                          onClick={() => navigate("/forgot-password")}
                        >
                          ← Resend Mail
                        </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;